import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { PlusCircle, Loader2, Trash2, AlertCircle, Search } from 'lucide-react';
import { isProjectOwner } from '../../lib/permissions';
import { useDebounce } from '../../hooks/useDebounce';

interface Editor {
  user_id: string;
  email: string;
  added_at: string;
}

interface SearchResult {
  email: string;
  user_id: string;
}

interface EditorsSectionProps {
  projectId: string;
}

export function EditorsSection({ projectId }: EditorsSectionProps) {
  const [editors, setEditors] = useState<Editor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newEditorEmail, setNewEditorEmail] = useState('');
  const [isAddingEditor, setIsAddingEditor] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebounce(newEditorEmail, 300);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);

  useEffect(() => {
    fetchEditors();
    checkPermissions();
  }, [projectId]);

  useEffect(() => {
    // Handle clicks outside the dropdown
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      searchUsers(debouncedSearch);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [debouncedSearch]);

  async function searchUsers(query: string) {
    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .rpc('search_users_by_email', { 
          email_query: query
        });

      if (error) throw error;

      // Filter out emails that are already editors
      const existingEmails = editors.map(editor => editor.email.toLowerCase());
      const filteredResults = (data || []).filter(
        result => !existingEmails.includes(result.email.toLowerCase())
      );

      setSearchResults(filteredResults);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsSearching(false);
    }
  }

  async function checkPermissions() {
    const isOwner = await isProjectOwner(projectId);
    setCanEdit(isOwner);
  }

  async function fetchEditors() {
    try {
      const { data: editorsData, error: editorsError } = await supabase
        .from('project_editors')
        .select(`
          user_id,
          added_at
        `)
        .eq('project_id', projectId);

      if (editorsError) throw editorsError;

      const editorsWithEmails = await Promise.all(
        editorsData.map(async (editor) => {
          const { data: emailData } = await supabase
            .rpc('get_auth_user_email', { user_id: editor.user_id });
          
          return {
            user_id: editor.user_id,
            email: emailData || 'Unknown Email',
            added_at: editor.added_at
          };
        })
      );

      setEditors(editorsWithEmails);
    } catch (error: any) {
      console.error('Error fetching editors:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddEditor(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedResult) {
      setError('Please select a user from the dropdown.');
      return;
    }

    setIsAddingEditor(true);
    setError(null);

    try {
      const { error: addError } = await supabase
        .from('project_editors')
        .insert({
          project_id: projectId,
          user_id: selectedResult.user_id,
          added_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (addError) {
        if (addError.code === '23505') {
          throw new Error('This user is already an editor of this project.');
        }
        throw addError;
      }

      setNewEditorEmail('');
      setSelectedResult(null);
      setShowDropdown(false);
      fetchEditors();
    } catch (error: any) {
      console.error('Error adding editor:', error);
      setError(error.message);
    } finally {
      setIsAddingEditor(false);
    }
  }

  async function handleRemoveEditor(userId: string) {
    try {
      const { error } = await supabase
        .from('project_editors')
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', userId);

      if (error) throw error;
      
      setEditors(editors.filter(editor => editor.user_id !== userId));
    } catch (error: any) {
      console.error('Error removing editor:', error);
      setError(error.message);
    }
  }

  const handleSelectResult = (result: SearchResult) => {
    setNewEditorEmail(result.email);
    setSelectedResult(result);
    setShowDropdown(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-base font-semibold leading-6 text-brand-teal">Project Editors</h2>
          <p className="mt-2 text-sm text-gray-700">
            Manage who can edit this project and its interviews
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {canEdit && (
        <form onSubmit={handleAddEditor} className="mt-6">
          <div className="flex gap-x-4">
            <div className="flex-grow relative">
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="email"
                  name="email"
                  id="email"
                  required
                  value={newEditorEmail}
                  onChange={(e) => {
                    setNewEditorEmail(e.target.value);
                    setSelectedResult(null);
                  }}
                  onFocus={() => {
                    if (searchResults.length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  className="block w-full rounded-lg border-gray-300 pl-10 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                  placeholder="Search by email address"
                />
                {isSearching && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              {showDropdown && searchResults.length > 0 && (
                <div 
                  ref={dropdownRef}
                  className="absolute z-10 mt-1 w-full rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5"
                >
                  <ul className="max-h-60 overflow-auto">
                    {searchResults.map((result) => (
                      <li
                        key={result.user_id}
                        className="cursor-pointer px-4 py-2 hover:bg-gray-100 text-sm"
                        onClick={() => handleSelectResult(result)}
                      >
                        {result.email}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={isAddingEditor || !selectedResult}
              className="inline-flex items-center rounded-lg bg-brand-orange px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Editor
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-brand-teal sm:pl-6">
                      Email
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-brand-teal">
                      Added
                    </th>
                    {canEdit && (
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {editors.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 3 : 2} className="py-8 text-center text-sm text-gray-500">
                        No editors added yet
                      </td>
                    </tr>
                  ) : (
                    editors.map((editor) => (
                      <tr key={editor.user_id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {editor.email}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(editor.added_at).toLocaleDateString()}
                        </td>
                        {canEdit && (
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button
                              onClick={() => handleRemoveEditor(editor.user_id)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Remove editor</span>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}